import {
  BoxProps,
  ButtonProps,
  Icon,
  Popover,
  Portal,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomerPortalAccessRuleTypeEnum,
  CustomerPortalAccountConfigurationForm,
  CustomerPortalAccountConfigurationFormSchema,
  CustomerPortalAccountConfigurationRequestSchema,
  IBillGroupRespUI,
} from '@monetize/types/app';
import {
  isBillingPlatformApiErrorResponse,
  Maybe,
} from '@monetize/types/common';
import { isAxiosError } from 'axios';
import { useEffect, useId, useMemo, useState } from 'react';
import {
  Control,
  Controller,
  FieldError,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { BsTrash } from 'react-icons/bs';
import { MdClose } from 'react-icons/md';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { handleApiErrorToast } from '../../api/axios';
import {
  useGetAuthenticatedUserSession,
  useGetCustomerPortalAccountConfig,
  useUpdateCustomerPortalAccountConfig,
} from '../../api/customerPortalService';
import { getCustomerPortalAuthenticatedPreviewRoute } from '../../constants/routes';
import { useACL } from '../../services/acl/acl';
import { useAuth } from '../../services/auth0';
import { useFlags } from '../../services/launchDarkly';
import { logger } from '../../services/logger';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MFormField,
  MInput,
  MText,
} from '../Monetize';
import { CustomerPortalShareLinkCopyButton } from './CustomerPortalShareLinkCopyButton';

function collectFormValues(values: string[]) {
  return values.map((email, idx) => ({
    id: idx,
    value: email,
  }));
}

interface CustomerPortalConfigPopoverProps {
  accountId: string;
  billGroup: IBillGroupRespUI;
  buttonProps?: ButtonProps;
  /** If copy to clipboard is not available or fails, link is shows as text */
  showLinkProps?: BoxProps;
}

export const CustomerPortalConfigPopover = ({
  accountId,
  billGroup,
  buttonProps,
  showLinkProps,
}: CustomerPortalConfigPopoverProps) => {
  const { customerPortalEnabled } = useFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const { open, onClose, onOpen } = useDisclosure();

  const customerPortalOpen = searchParams.get('customerPortalOpen') === 'true';

  useEffect(() => {
    if (customerPortalEnabled && customerPortalOpen) {
      onOpen();
      searchParams.delete('customerPortalOpen');
      setSearchParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!customerPortalEnabled) {
    return null;
  }

  return (
    <Popover.Root
      open={open}
      variant="popover"
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
          onOpen();
        } else {
          onClose();
        }
      }}
    >
      <Popover.Trigger asChild>
        <MButton variant="tertiary">Customer Portal</MButton>
      </Popover.Trigger>
      {open && (
        <Portal>
          <CustomerPortalConfigPopoverContent
            accountId={accountId}
            billGroup={billGroup}
            buttonProps={buttonProps}
            showLinkProps={showLinkProps}
          />
        </Portal>
      )}
    </Popover.Root>
  );
};

const CustomerPortalConfigPopoverContent = ({
  accountId,
  billGroup,
  buttonProps,
  showLinkProps,
}: CustomerPortalConfigPopoverProps) => {
  const titleId = useId();
  const billGroupId = billGroup.id;
  const preAllowedEmailAddresses = useMemo(
    () =>
      Array.from(
        new Set(
          [
            billGroup.billingContact?.email,
            billGroup.shippingContact?.email,
            ...(billGroup.ccEmails || []),
          ]
            .filter(Boolean)
            .sort(),
        ),
      ).join(', '),
    [billGroup.ccEmails, billGroup.billingContact, billGroup.shippingContact],
  );

  const { tenantId } = useAuth();
  const navigate = useNavigate();

  const { canDo } = useACL();
  const canUpdateAccount = canDo([['accounts', 'update']]);

  const [isEditMode, setIsEditMode] = useState(false);

  const { data: currentConfiguration, isLoading } =
    useGetCustomerPortalAccountConfig(billGroupId);

  const { mutateAsync: updatePortalConfig, isPending: isSaveLoading } =
    useUpdateCustomerPortalAccountConfig();

  const {
    mutateAsync: getAuthenticatedUserSession,
    isPending: isSessionLoading,
  } = useGetAuthenticatedUserSession({ meta: { showErrorToast: true } });

  const {
    control,
    setValue,
    formState: { errors },
    setError,
    handleSubmit,
  } = useForm<CustomerPortalAccountConfigurationForm>({
    resolver: zodResolver(CustomerPortalAccountConfigurationFormSchema),
    defaultValues: {
      domainAllowlist: collectFormValues(
        currentConfiguration?.domainAllowlist || [],
      ),
      emailAllowlist: collectFormValues(
        currentConfiguration?.emailAllowlist || [],
      ),
    },
  });

  const {
    fields: domainFields,
    append: appendDomainField,
    remove: removeDomainField,
  } = useFieldArray({
    control,
    name: 'domainAllowlist',
  });

  const {
    fields: emailFields,
    append: appendEmailField,
    remove: removeEmailField,
  } = useFieldArray({
    control,
    name: 'emailAllowlist',
  });

  useEffect(() => {
    if (!isEditMode && currentConfiguration) {
      setValue(
        'domainAllowlist',
        collectFormValues(currentConfiguration.domainAllowlist || []),
      );
      setValue(
        'emailAllowlist',
        collectFormValues(currentConfiguration.emailAllowlist || []),
      );
    }
  }, [currentConfiguration, isEditMode, setValue]);

  function handleCancel() {
    // reset to original values
    setValue(
      'domainAllowlist',
      collectFormValues(currentConfiguration?.domainAllowlist || []),
    );
    setValue(
      'emailAllowlist',
      collectFormValues(currentConfiguration?.emailAllowlist || []),
    );
    setIsEditMode(false);
  }

  async function handleSave(data: CustomerPortalAccountConfigurationForm) {
    try {
      const rawPayload = {
        accessRules: [
          ...data.domainAllowlist.map(({ value: domain }) => ({
            type: CustomerPortalAccessRuleTypeEnum.DOMAIN,
            value: domain,
          })),
          ...data.emailAllowlist.map(({ value: email }) => ({
            type: CustomerPortalAccessRuleTypeEnum.EMAIL,
            value: email,
          })),
        ],
      };
      const payload =
        CustomerPortalAccountConfigurationRequestSchema.parse(rawPayload);

      await updatePortalConfig({ billGroupId, payload });

      setValue('emailAllowlist', data.emailAllowlist);
      setValue('domainAllowlist', data.domainAllowlist);
      setIsEditMode(false);
    } catch (ex) {
      logger.error('Failed to save customer portal configuration', ex);
      handleResponseError(ex, data);
    }
  }

  function handleResponseError(
    errorResponse: unknown,
    data: CustomerPortalAccountConfigurationForm,
  ) {
    try {
      let foundScopedError = false;
      const response = isAxiosError(errorResponse)
        ? errorResponse.response?.data
        : errorResponse;
      if (
        isBillingPlatformApiErrorResponse(response) &&
        Array.isArray(response.error.context?.errors)
      ) {
        response.error.context.errors.forEach((error) => {
          if (error.type === CustomerPortalAccessRuleTypeEnum.DOMAIN) {
            foundScopedError = true;
            setError(
              `domainAllowlist.${data.domainAllowlist.length - 1}.value`,
              {
                type: 'manual',
                message: error.details,
              },
            );
          }
          if (error.type === CustomerPortalAccessRuleTypeEnum.EMAIL) {
            foundScopedError = true;
            setError(`emailAllowlist.${data.emailAllowlist.length - 1}.value`, {
              type: 'manual',
              message: error.details,
            });
          }
        });
      }
      if (!foundScopedError) {
        handleApiErrorToast(response);
      }
    } catch (error) {
      handleApiErrorToast(error);
    }
  }

  async function navigateToPreview(event: React.MouseEvent<HTMLButtonElement>) {
    try {
      const {
        billingPortalSession: { id: sessionId },
      } = await getAuthenticatedUserSession({ accountId, billGroupId });

      const route = getCustomerPortalAuthenticatedPreviewRoute({
        accountId,
        billGroupId,
        sessionId,
        tenantId,
      });

      if (event.ctrlKey || event.metaKey) {
        window.open(route, '_blank', 'noopener,noreferrer');
      } else {
        navigate(route);
      }
    } catch (ex) {
      logger.error('Failed to navigate to customer portal preview', ex);
    }
  }

  return (
    <Popover.Positioner>
      {/* zag only links the title if it's in the DOM one frame after
          Popover.Root mounts. This content mounts on open, so wire the
          name up by hand. */}
      <Popover.Content aria-labelledby={titleId}>
        <Popover.Header>
          <Popover.Title id={titleId}>Customer Portal</Popover.Title>
        </Popover.Header>
        {/* v3's trigger renders no icon and zag names it "close"; v2's
            PopoverCloseButton was an X named "Close". */}
        <Popover.CloseTrigger aria-label="Close">
          <Icon asChild>
            <MdClose />
          </Icon>
        </Popover.CloseTrigger>
        <Popover.Arrow mt="1" />
        <Popover.Body px={2}>
          {isLoading ? (
            <Spinner size="md" />
          ) : (
            <form onSubmit={handleSubmit(handleSave)}>
              <MText mb={4}>
                Configure access to the customer portal by allowing specific
                email addresses or email domains.
              </MText>
              {preAllowedEmailAddresses && (
                <MText mb={4}>
                  Your Bill Group contacts are automatically allowed access:{' '}
                  {preAllowedEmailAddresses}.
                </MText>
              )}
              <MBox mb={4}>
                {domainFields.length === 0 && (
                  <MFormField label="Allowed Domains">
                    <MText>No allowed domains configured.</MText>
                  </MFormField>
                )}
                {domainFields.map((field, index) => (
                  <EmailOrDomainRow
                    key={field.id}
                    label="Allowed Domains"
                    index={index}
                    isEditMode={isEditMode}
                    type="domainAllowlist"
                    error={errors?.domainAllowlist?.[index]?.value}
                    control={control}
                    onRemove={removeDomainField}
                  />
                ))}
                {isEditMode && (
                  <MButton
                    mt={2}
                    ml={-2}
                    variant="tertiary"
                    type="button"
                    size="sm"
                    onClick={() =>
                      appendDomainField({ id: domainFields.length, value: '' })
                    }
                  >
                    + Add Domain
                  </MButton>
                )}
              </MBox>
              {emailFields.length === 0 && (
                <MFormField label="Allowed Email Addresses">
                  <MText>No allowed email addresses configured.</MText>
                </MFormField>
              )}
              {emailFields.map((field, index) => (
                <EmailOrDomainRow
                  key={field.id}
                  label="Allowed Email Addresses"
                  index={index}
                  isEditMode={isEditMode}
                  type="emailAllowlist"
                  error={errors?.emailAllowlist?.[index]?.value}
                  control={control}
                  onRemove={removeEmailField}
                />
              ))}
              {isEditMode && (
                <MButton
                  mt={2}
                  ml={-2}
                  variant="tertiary"
                  type="button"
                  size="sm"
                  onClick={() =>
                    appendEmailField({ id: emailFields.length, value: '' })
                  }
                >
                  + Add Email Address
                </MButton>
              )}

              <MFlex mt={4}>
                {!isEditMode && canUpdateAccount && (
                  <MButton
                    variant="secondary"
                    type="button"
                    size="sm"
                    onClick={() => setIsEditMode(true)}
                  >
                    Edit
                  </MButton>
                )}
                {isEditMode && canUpdateAccount && (
                  <>
                    <MButton
                      variant="cancel"
                      type="button"
                      size="sm"
                      mr={2}
                      onClick={handleCancel}
                      isDisabled={isSaveLoading}
                    >
                      Cancel
                    </MButton>
                    <MButton
                      variant="primary"
                      type="submit"
                      size="sm"
                      disabled={isSaveLoading}
                      isLoading={isSaveLoading}
                    >
                      Save
                    </MButton>
                  </>
                )}
              </MFlex>
            </form>
          )}
        </Popover.Body>
        <Popover.Footer
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={0}
          pt={2}
          pb={0}
          mx={0}
        >
          <MButton
            variant="tertiary"
            type="button"
            size="sm"
            onClick={(event) => navigateToPreview(event)}
            isDisabled={isSessionLoading}
            isLoading={isSessionLoading}
          >
            Preview Portal
          </MButton>
          <CustomerPortalShareLinkCopyButton
            accountId={accountId}
            billGroupId={billGroupId}
            beforeCopyText="Copy Portal Link"
            buttonProps={buttonProps}
            showLinkProps={showLinkProps}
          />
        </Popover.Footer>
      </Popover.Content>
    </Popover.Positioner>
  );
};

export const EmailOrDomainRow = ({
  label,
  index,
  isEditMode,
  type,
  error,
  control,
  onRemove,
}: {
  label: string;
  index: number;
  isEditMode: boolean;
  type: 'domainAllowlist' | 'emailAllowlist';
  error: Maybe<FieldError>;
  control: Control<CustomerPortalAccountConfigurationForm>;
  onRemove: (index: number) => void;
}) => {
  return (
    <MFlex alignItems={'end'}>
      <MFormField
        label={index > 0 ? undefined : label}
        error={error}
        mt={index > 0 ? 2 : 0}
      >
        <Controller
          name={`${type}.${index}.value`}
          control={control}
          render={({ field }) => (
            <MInput
              {...field}
              isDisabled={!isEditMode}
              isReadOnly={!isEditMode}
              variant={isEditMode ? 'outline' : 'readonly'}
            />
          )}
        />
      </MFormField>
      {isEditMode && (
        <MCustomIconButton
          icon={BsTrash}
          ml={1}
          mb={error ? 7 : 1}
          aria-label="Remove Item"
          onClick={() => onRemove(index)}
          isDisabled={!isEditMode}
        />
      )}
    </MFlex>
  );
};
